import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { CreditCard, Receipt, WalletCards } from 'lucide-react'
import { apiRequest } from '@/lib/api-client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

const usageSchema = z.object({
  state: z.object({
    plan: z.string(),
    llmCostEur: z.number(),
    budgetAvailableEur: z.number(),
    topupCreditsEur: z.number(),
  }),
})
const invoicesSchema = z.object({
  invoices: z.array(z.object({
    id: z.string(),
    number: z.string().nullable(),
    status: z.string().nullable(),
    hosted_invoice_url: z.string().nullable(),
    amount_paid: z.number(),
    currency: z.string(),
    created: z.number(),
  })),
})

export function SettingsBillingPage() {
  const usage = useQuery({ queryKey: ['billing-usage'], queryFn: () => apiRequest({ path: '/billing/usage', schema: usageSchema }) })
  const invoices = useQuery({ queryKey: ['billing-invoices'], queryFn: () => apiRequest({ path: '/billing/invoices', schema: invoicesSchema }) })
  const state = usage.data?.state

  return (
    // R1: ohne doppelte Aussenpadding-Schicht — mobil 16px Rand.
    <div className="mx-auto w-full max-w-[1180px] space-y-6 px-4 py-6 min-[761px]:px-8 min-[761px]:py-8">
      <header>
        <h1 className="text-[26px] font-[650] leading-tight tracking-tight text-foreground">Abrechnung</h1>
        <p className="mt-1 text-sm text-muted-foreground">Plan, Guthaben und Rechnungen.</p>
      </header>

      <div className="rounded-md border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
        MindKeep läuft aktuell im Prototyp-Betrieb — die Nutzung ist kostenlos. Plan-Upgrades und
        individuelle Vereinbarungen: <a href="mailto:hartmut@huebner.io" className="text-primary underline">hartmut@huebner.io</a>
      </div>

      <div className="grid gap-4 min-[761px]:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[16px] font-[650]"><CreditCard className="h-4 w-4 shrink-0" />Aktueller Plan</CardTitle>
            <CardDescription>Manuelles Billing — Käufe sind hier nicht aktiv.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-2xl font-semibold">{state?.plan ?? '...'}</p>
            <p className="text-sm text-muted-foreground">KI-Budget: {money(state?.llmCostEur ?? 0)} / {money(state?.budgetAvailableEur ?? 0)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[16px] font-[650]"><WalletCards className="h-4 w-4 shrink-0" />KI-Guthaben</CardTitle>
            <CardDescription>Guthaben verfällt nicht — ausser bei Vertragskündigung.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-2xl font-semibold">{money(state?.topupCreditsEur ?? 0)}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[16px] font-[650]"><Receipt className="h-4 w-4 shrink-0" />Letzte Rechnungen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {(invoices.data?.invoices ?? []).map((invoice) => (
            <a key={invoice.id} href={invoice.hosted_invoice_url ?? '#'} className="flex flex-wrap justify-between gap-x-4 gap-y-1 rounded-md border p-3 text-sm hover:bg-muted/50">
              <span className="min-w-0 break-all">{invoice.number ?? invoice.id}</span>
              <span>{(invoice.amount_paid / 100).toFixed(2)} {invoice.currency.toUpperCase()}</span>
            </a>
          ))}
          {invoices.data?.invoices.length === 0 ? <p className="text-sm text-muted-foreground">Noch keine Rechnungen.</p> : null}
        </CardContent>
      </Card>
    </div>
  )
}

function money(value: number): string {
  if (!Number.isFinite(value)) return 'unlimitiert'
  // M5-A: Backend misst ehrlich in EUR — keine Fake-CHF-Umrechnung.
  return `${value.toFixed(2)} EUR`
}
